<template>
    <div :class="embedded ? 'nch--glossary nch--glossary__embedded' : 'container py-4 nch--glossary'">
        <div v-if="preview" class="alert alert-info py-2 mb-3">{{ $t('glossary.preview_notice') }}</div>

        <!-- Detalhe do termo -->
        <div v-if="selected" class="card shadow-sm">
            <div class="card-body">
                <a href="#" class="d-inline-block mb-3" :style="{ color: color.primary }" @click.prevent="selected = null">
                    <i class="fas fa-arrow-left mr-1"></i>{{ $t('glossary.back') }}
                </a>
                <div class="d-flex align-items-center flex-wrap mb-1">
                    <h2 class="mb-0 mr-3">{{ selected.term }}</h2>
                    <span v-if="selected.category" class="badge badge-pill badge-light border">{{ selected.category.name }}</span>
                </div>
                <div class="text-muted small mb-3">
                    {{ selected.scope === 'course' ? selected.courses.map(c => c.name).join(', ') : $t('glossary.general') }}
                </div>
                <div class="nch--glossary-html" v-html="selected.definition"></div>
                <template v-if="selected.examples && stripHtml(selected.examples)">
                    <h6 class="mt-4 text-uppercase small font-weight-bold text-muted">{{ $t('glossary.examples') }}</h6>
                    <div class="nch--glossary-html" v-html="selected.examples"></div>
                </template>
                <template v-if="selected.synonyms && selected.synonyms.length">
                    <h6 class="mt-4 text-uppercase small font-weight-bold text-muted">{{ $t('glossary.synonyms') }}</h6>
                    <span v-for="s in selected.synonyms" :key="s" class="badge badge-light border mr-1 mb-1 font-weight-normal">{{ s }}</span>
                </template>
                <template v-if="selected.related && selected.related.length">
                    <h6 class="mt-4 text-uppercase small font-weight-bold text-muted">{{ $t('glossary.related') }}</h6>
                    <a
                        v-for="r in selected.related"
                        :key="r.id"
                        href="#"
                        class="badge badge-pill mr-1 mb-1 font-weight-normal text-white"
                        :style="{ backgroundColor: color.primary }"
                        @click.prevent="open(r.id)"
                    >{{ r.term }}</a>
                </template>
            </div>
        </div>

        <!-- Lista -->
        <template v-else>
            <div v-if="!embedded" class="d-flex align-items-center flex-wrap mb-3">
                <div class="mr-auto">
                    <h1 class="h3 mb-0">{{ $t('glossary.title') }}</h1>
                    <div class="text-muted small">{{ $t('glossary.subtitle') }}</div>
                </div>
            </div>

            <div class="card shadow-sm mb-3">
                <div class="card-body py-3">
                    <div class="form-row align-items-center">
                        <div class="col-md-7 mb-2 mb-md-0">
                            <div class="input-group">
                                <div class="input-group-prepend"><span class="input-group-text bg-white"><i class="fas fa-search text-muted"></i></span></div>
                                <input v-model.trim="search" type="search" class="form-control nch--search-input" :placeholder="$t('glossary.search')" :aria-label="$t('glossary.search')">
                            </div>
                        </div>
                        <div class="col-md-5">
                            <select v-model="categoryId" class="custom-select" :aria-label="$t('glossary.category')">
                                <option :value="null">{{ $t('glossary.all_categories') }}</option>
                                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="nch--letters mt-3" role="tablist">
                        <button
                            type="button"
                            class="btn btn-sm mr-1 mb-1"
                            :class="letter === 'ALL' ? 'text-white' : 'btn-light'"
                            :style="letter === 'ALL' ? { backgroundColor: color.primary } : {}"
                            @click="letter = 'ALL'"
                        >{{ $t('glossary.all') }}</button>
                        <button
                            v-for="l in alphabet"
                            :key="l"
                            type="button"
                            class="btn btn-sm mr-1 mb-1 nch--letter"
                            :class="letter === l ? 'text-white' : (letters.includes(l) ? 'btn-light' : 'btn-light text-muted nch--letter__empty')"
                            :style="letter === l ? { backgroundColor: color.primary } : {}"
                            :disabled="!letters.includes(l)"
                            @click="letter = l"
                        >{{ l }}</button>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="text-center text-muted py-5"><i class="fas fa-circle-notch fa-spin fa-2x"></i></div>
            <div v-else-if="error" class="alert alert-warning">{{ error }}</div>
            <div v-else-if="!terms.length" class="card shadow-sm"><div class="card-body text-center text-muted py-5">{{ $t('glossary.empty') }}</div></div>
            <div v-else class="card shadow-sm">
                <div class="list-group list-group-flush">
                    <a
                        v-for="t in terms"
                        :key="t.id"
                        href="#"
                        class="list-group-item list-group-item-action nch--term"
                        @click.prevent="open(t.id)"
                    >
                        <div class="d-flex align-items-start">
                            <span class="nch--term-letter mr-3" :style="{ color: color.primary }">{{ t.letter }}</span>
                            <div class="flex-grow-1 min-w-0">
                                <div class="d-flex align-items-center flex-wrap">
                                    <strong class="mr-2">{{ t.term }}</strong>
                                    <span v-if="t.category" class="badge badge-pill badge-light border font-weight-normal">{{ t.category.name }}</span>
                                </div>
                                <div class="text-muted small nch--term-excerpt">{{ excerpt(t.definition) }}</div>
                            </div>
                            <i class="fas fa-chevron-right text-muted mt-2"></i>
                        </div>
                    </a>
                </div>
                <div v-if="total > perPage" class="card-footer bg-white d-flex justify-content-between align-items-center">
                    <button class="btn btn-sm btn-light" :disabled="page <= 1" @click="page--">&lsaquo;</button>
                    <span class="small text-muted">{{ page }} / {{ Math.ceil(total / perPage) }}</span>
                    <button class="btn btn-sm btn-light" :disabled="page >= Math.ceil(total / perPage)" @click="page++">&rsaquo;</button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
/**
 * Glossário na área do aluno (backlog#342): busca, navegação alfabética,
 * filtro por categoria e detalhe do termo (definição, exemplos, sinônimos,
 * termos relacionados). Só mostra o que a API libera para o aluno logado.
 */
export default {
    name: 'Glossary',

    props: {
        preview: { type: Boolean, default: false },
        courseId: { type: Number, default: null },
        /** dentro da aba Glossário do curso: sem container nem título */
        embedded: { type: Boolean, default: false },
    },

    data: () => ({
        search: '',
        letter: 'ALL',
        categoryId: null,
        page: 1,
        perPage: 50,
        total: 0,
        terms: [],
        letters: [],
        categories: [],
        selected: null,
        loading: true,
        error: null,
        debounce: null,
    }),

    computed: {
        color() { return this.$store.getters.COLOR || {}; },
        alphabet() { return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''); },
    },

    watch: {
        search() { clearTimeout(this.debounce); this.debounce = setTimeout(() => { this.page = 1; this.load(); }, 350); },
        letter() { this.page = 1; this.load(); },
        categoryId() { this.page = 1; this.load(); },
        page() { this.load(); },
    },

    created() {
        const params = new URLSearchParams(window.location.search);
        const term = params.get('term');
        this.load().then(() => { if (term && /^\d+$/.test(term)) this.open(Number(term)); });
    },

    methods: {
        params(extra = {}) {
            return {
                search: this.search || undefined,
                letter: this.letter !== 'ALL' ? this.letter : undefined,
                category_id: this.categoryId || undefined,
                course_id: this.courseId || undefined,
                page: this.page,
                per_page: this.perPage,
                preview: this.preview ? 1 : undefined,
                ...extra,
            };
        },

        load() {
            this.loading = true;
            this.error = null;
            return window.axios.get('/api/app/glossary', { params: this.params() })
                .then(({ data }) => {
                    this.terms = data.terms || [];
                    this.total = data.total || 0;
                    this.perPage = data.per_page || this.perPage;
                    this.letters = data.letters || [];
                    this.categories = data.categories || [];
                })
                .catch((e) => {
                    const res = (e && e.response) || {};
                    this.error = (res.data && (res.data.error || res.data.message)) || this.$t('glossary.disabled');
                })
                .finally(() => { this.loading = false; });
        },

        open(id) {
            window.axios.get(`/api/app/glossary/${id}`, { params: { preview: this.preview ? 1 : undefined } })
                .then(({ data }) => { this.selected = data; window.scrollTo({ top: 0, behavior: 'smooth' }); })
                .catch(() => {});
        },

        stripHtml(html) {
            const div = document.createElement('div');
            div.innerHTML = html || '';
            return (div.textContent || '').trim();
        },

        excerpt(html) {
            const text = this.stripHtml(html);
            return text.length > 160 ? `${text.slice(0, 160)}…` : text;
        },
    },
};
</script>

<style scoped>
.nch--term-letter { font-weight: 700; font-size: 1.1rem; width: 1.4rem; text-align: center; }
.nch--term-excerpt { overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.nch--letter { min-width: 2.1rem; font-weight: 600; }
.nch--letter__empty { opacity: 0.45; }
.min-w-0 { min-width: 0; }
.nch--glossary__embedded { padding-top: 1rem; }
.nch--glossary-html :deep(p:last-child) { margin-bottom: 0; }
</style>
